import { useEffect, useRef } from 'react';
import ReactGA from 'react-ga';
import _ from 'lodash';
import qs from 'query-string';
import remoteEvent from '@tools/remoteEvent';
import md5 from 'md5';
import {
  FORBIDDEN_COUNTRIES_FOR_USE,
  SIGNIN_ACCOUNT_TRACKING_KEY,
  DEFAULT_TRACK_SRC,
} from './constants';

// 简单的对比 props
export function areEqual(prevProps, nextProps) {
  if (!_.isEqual(prevProps, nextProps)) {
    return true;
  }
  return false;
}

// md5 加密
export function loopCrypto(str, time) {
  const salt = '_kucoin_';
  const c = md5(`${salt}${str}${salt}`).toString();
  if (time <= 0) {
    return c;
  }
  return loopCrypto(c, time - 1);
}

// 判断是否有 没有完成的倒计时
export function getLocalTime({ localCountTimeKey, countTimeKey } = {}) {
  let localTime = localStorage.getItem(localCountTimeKey) || 0;
  const countTime = localStorage.getItem(countTimeKey);
  if (localTime) {
    const diffTime = Math.floor((+new Date() - localTime) / 1000);
    localTime = diffTime < countTime + 2 ? countTime - diffTime : 0;
    if (localTime === 0) {
      localStorage.removeItem(localCountTimeKey);
      localStorage.removeItem(countTimeKey);
    }
  }
  return localTime;
}

export const resolveContryCode = (code) => {
  return (code || '').replace(/_.*$/g, '');
};

export const removeSpaceSE = (str) => {
  return (str || '').replace(/(^\s*)|(\s*$)/g, '');
};

// 常用正则
export const REGEXP = {
  pwd: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[\S]{10,32}$/, // 至少包含大小写字母跟数字，不支持空格
  email: /(?:[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/, // eslint-disable-line
  phone: /^\d+$/,
};

export const REGEXP_PWD_GROUP = {
  length: /^.{10,32}$/,
  str: /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/,
  space: /\s/,
};

// zendesk 登录处理
export const getJWTPath = (platform, jwt, returnTo) => {
  if (platform === 'zendesk') {
    let zendesk = `https://kucoin.zendesk.com/access/jwt?jwt=${jwt}`;
    if (returnTo) {
      zendesk = `${zendesk}&return_to=${returnTo}`;
    }
    return zendesk;
  }

  return null;
};

// 获取query参数，兼容hash路由
export function parseQuery() {
  if (typeof window !== 'undefined') {
    const { href } = window.location;
    if (href.indexOf('?') === -1) {
      return {};
    }

    const queryString = href.split('?')[1] || '';

    return qs.parse(queryString);
  }
  return {};
}

// const utmMap = {
//   thirdPartClient: 'utm_source',
//   utmCampaign: 'utm_campaign',
//   utmMedium: 'utm_medium',
//   referralCode: 'rcode',
// };

export function transformObjWithMap(obj, keyMap) {
  return Object.keys(keyMap).reduce((result, key) => {
    const transformKey = keyMap[key];
    if (typeof obj[transformKey] !== 'undefined') {
      result[key] = obj[transformKey];
    }

    return result;
  }, {});
}

/**
 * 判断是否子账号
 */
export const checkIsSub = (obj = {}) => {
  const { isSub = false, type = 1 } = obj || {};
  return isSub || type === 3;
};

// export function getUtm() {
//   const option = {};
//   const fields = Object.keys(utmMap);
//   _.forEach(fields, (v) => {
//     const key = utmMap[v];
//     const _key = `kucoinv2_${key}`;
//     let utm = localStorage.getItem(_key);
//     if (utm) {
//       try {
//         utm = JSON.parse(utm);
//         if (utm && utm !== 'undefined') {
//           option[v] = utm;
//         }
//       } catch (e) {
//         console.log(e);
//       }
//     }
//   });
//   return option;
// }

/**
 * hook: 用于使用上一个value
 * @param {*} value
 * @returns
 */
export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

/**
 * 自定义hook
 * 适用于language变化后，触发form校验，更新form界面相关数据
 * (如language变化后，更新errorInfo)
 * 当语言切换后，validateTrigger: ['onBlur', 'onSubmit'] 不会触发校验
 * @param {*} form form实例
 * @param {*} language 语言code 如zh_CN
 * @param {*} source 本方法调用来源（暂未使用）
 * @param {*} fields 需要校验的fields ['a','b']
 */
export const useFormValidateByLang = (form, language, source, fields) => {
  const formRef = useRef(null);
  formRef.current = form;
  const previouseLanguage = usePrevious(language);
  useEffect(() => {
    if (language && previouseLanguage && previouseLanguage !== language) {
      // 只在用户主动切换后，才进行校验
      if (!fields) {
        const errorInfo = formRef.current.getFieldsError();
        fields = _.keys(errorInfo).filter((key) => !!errorInfo[key]);
      }
      formRef.current && formRef.current.validateField(fields);
    }
  }, [language, previouseLanguage, form]);
};

/**
 * 合并埋点动作
 * eventLevelVariables: 事件级别变量 eg: {ad_pop_id: '1', ad_pop_module: 'assets'}
 */
export const ga = async (key, eventLevelVariables) => {
  if (!key) return;
  if (window._hmt && typeof window._hmt.push === 'function') {
    window._hmt.push(['_trackEvent', key, 'click']);
  }
  if (typeof window.gio === 'function') {
    window.gio('track', key, eventLevelVariables);
  }
  // eslint-disable-next-line no-unused-expressions
  remoteEvent.emit(remoteEvent.evts.GET_REPORT, (Report) => {
    Report.logAction(key, 'click');
  });
  ReactGA.event({ category: key, action: 'click' }); // react-ga
};

// /**
//  * 获取携带相关属性的node节点
//  *
//  * @param {HTMLNode} node node节点
//  * @param {string} attr 查询的属性
//  */
// export const getGaElement = (node, attr) => {
//   if (!node || node.localName === 'body') return;
//   const key = node.getAttribute(attr);
//   if (key) {
//     return key;
//   }
//   if (!node.parentNode) return;
//   return getGaElement(node.parentNode, attr);
// };

export const isForbiddenCountry = (code, field = 'mobileCode') => {
  return _.find(FORBIDDEN_COUNTRIES_FOR_USE, (forbiddenItem) => {
    return forbiddenItem[field] === code;
  });
};

export const kcsensorsClick = (spm, data = {}) => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    sensors.trackClick(spm, data);
  });
};

export const getPreSpmCode = () => {
  let code = '';
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    code = sensors.spm.getPreSpmCode();
  });
  return code;
};

export const validateSpm = (spm) => {
  let validated = false;
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    validated = sensors.spm.validateSpm(spm);
  });
  return validated;
};

export const kcsensorsManualTrack = (trackingConfig, type = 'expose') => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    if (!trackingConfig) return;
    const { spm = [], data = {}, checkID = true } = trackingConfig || {};
    let { kc_siteid, kc_pageid } = trackingConfig;

    const spmModule = sensors.spm;
    if (!kc_siteid) {
      kc_siteid = spmModule.getSiteId();
    }
    if (!kc_pageid) {
      kc_pageid = spmModule.getPageId();
    }

    if (checkID) {
      if (!kc_pageid || !validateSpm(spm)) return;
    }
    let exposeCfg = {
      ...data,
    };
    if (checkID) {
      exposeCfg = {
        spm_id: spmModule.compose(spm),
        site_id: kc_siteid,
        page_id: kc_pageid,
        pre_spm_id: getPreSpmCode(),
        ...exposeCfg,
      };
    }
    sensors.track(type, exposeCfg);
  });
};

/**
 * 登录/注册时或者页面来源，从哪个界面触发的登录/注册（便于统计流量）
 * 取值优先级如下
 *    组件传入的source -> url上面的source参数 -> 当前界面的page_id - 默认mainSet主站
 *    其中url带的source是其他二级域名跳转主站登录的情况 格式如下 /signin?spm=kcWeb.B2RewardsHub.CDiversion.1
 *
 * @param {*} config trackingConfig组件调用方传过来的source
 * @returns
 */
export const getTrackingSource = (config = {}) => {
  const { source } = config || {};
  let sdkPageID = '';
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    sdkPageID = sensors.spm.getPageId();
  });
  const urlSource = getUrlSource();
  return urlSource || source || sdkPageID || DEFAULT_TRACK_SRC;
};

const getUrlSource = () => {
  const { spm } = parseQuery();
  if (spm && _.size(spm) > 0 && _.includes(spm, '.')) {
    const spmList = _.split(spm, '.');
    return spmList[1];
  }
};

// 用于获取神策匿名ID
export const getAnonymousID = () => {
  let id = '';
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    id = sensors.getAnonymousID();
  });
  return id;
};

export const sensorsLogin = (uid, userLevel) => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    sensors.login(uid, userLevel);
  });
};

// 获取神策 ab 结果
export const getSensorsABResult = (options) => {
  const _config = options || {};
  const getABFunc = _.get(window, '$KcSensors.fastFetchABTest');
  if (getABFunc) {
    return getABFunc(_config);
  }
  return Promise.resolve(_config.default_value);
};

/**
 * 从其他工程移植过来
 * @description 获取queryString字符串转换为JSON对象
 * @param {String} search 可选参数 无是自动获取浏览器后面的queryString
 * @returns {Object}
 * runtime: next/browser
 */
export function searchToJson(search) {
  if (!search) {
    search = window.location.search.slice(1);
  }
  const temp = {};
  if (search) {
    try {
      const arr = search.split('&');
      Object.keys(arr).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(arr, key)) {
          const str = arr[key];
          const at = str.indexOf('=');
          const k = str.substring(0, at);
          const v = decodeURIComponent(decodeURI(str.substring(at + 1)));
          temp[k] = v;
        }
      });
    } catch (e) {
      console.log(e);
    }
  }

  return temp;
}

// 获取默认显示 tab
export const getDefaultSign = () => {
  let _isDefaultEmail = true;

  try {
    const query = searchToJson();
    const { type = '' } = query || {};
    if (type === 'phone') {
      _isDefaultEmail = false;
    }
  } catch (e) {
    //
  }

  return _isDefaultEmail ? 'sign.email.tab' : 'sign.phone.tab';
};

// 判断登录是否需要邮件授权
export const checkIsNeedMailAuthorize = ({ riskTag }) => riskTag === 'LOGIN_RISK_EMAIL_VERIFY';
